import createExposedPromise, {ExposedPromise} from './createExposedPromise';

export default function createAsyncMutexWithResolver<Args extends any[], T>(
    criticalSection: (...args: Args) => Promise<T>,
    resolver: (...args: Args) => string = () => '',
): (...args: Args) => Promise<T> {

    const mutexes: Record<string, (...args: Args) => Promise<T>> = {};
    return (...args: Args) => {
        const key = resolver(...args);
        if (!mutexes[key]) {
            mutexes[key] = createAsyncMutex(criticalSection);
        }
        return mutexes[key](...args);
    };
}

function createAsyncMutex<Args extends any[], T>(
    criticalSection: (...args: Args) => Promise<T>
): (...args: Args) => Promise<T> {

    let lock: Promise<T> | undefined;
    let queue: CoalescedQueue<Args, T> | undefined;

    return async function mutex(...args: Args): Promise<T> {

        // The mutex is unlocked.
        if (!lock) {
            lock = criticalSection(...args);
            const output = await lock;
            lock = undefined;
            return output;

        // The mutuex is locked but nobody else is in the queue
        } else if (!queue) {
            queue = createCoalescedQueue(args);
            await lock;
            const prevQueue = queue;
            queue = undefined;
            const output = await mutex(...prevQueue.args);
            prevQueue.resolve(output);
            return output;

        // The mutuex is locked and others are in the queue
        } else {
            queue.args = args;
            return await queue.promise;
        }
    };
}

interface CoalescedQueue<Args extends any[], T> extends ExposedPromise<T> {
    args: Args;
}

function createCoalescedQueue<Args extends any[], T>(args: Args): CoalescedQueue<Args, T>  {
    return {args, ...createExposedPromise()};
}
